/**
 * Case studies. Every factual claim here is observable on the live site or
 * was confirmed from its rendered markup.
 *
 * NOTE ON RESULTS: `outcomes` is deliberately empty. Numbers like "conversions
 * up 40%" are the single most valuable thing on a case study page and the
 * single worst thing to invent, so they are left for the client-approved
 * figures. Fill them in and they render automatically; leave them and the
 * section simply does not appear.
 *
 * CommonJS: read by webpack and by scripts/generate-seo.js.
 */
module.exports = [
  {
    slug: 'farmer-boaz',
    name: 'Farmer Boaz',
    url: 'https://farmerboaz.com/',
    sector: 'Agriculture & food',
    year: '2026',
    shot: 'farmerboaz',
    tagline: 'A community meat-share platform connecting Alberta farms to Calgary tables',
    metaTitle: 'Farmer Boaz Case Study | Codelithic Labs',
    metaDescription:
      'How we built Farmer Boaz, a campaign-based community meat-share platform connecting local farms with Calgary families ordering together.',
    summary:
      'Farmer Boaz sells farm-raised meat as community shares. A farm opens a campaign, neighbours order together to fill it, and the order is processed and delivered as one batch. That group-buying model is the whole product, and nothing off the shelf does it.',
    brief: [
      'The idea was straightforward and the mechanics were not. Ordinary e-commerce assumes one customer buying one thing from stock on hand. Here, orders accumulate against a campaign that has a target and a deadline, the product does not exist yet when someone orders, and everyone in the campaign is processed and delivered together once it closes.',
      'We built the campaign model first, then the storefront around it. Customers pick a share size that fits their freezer, the campaign tracks toward its target, and once it closes everything moves to processing and delivery on a shared timeline.',
    ],
    built: [
      { title: 'Campaign-based ordering', desc: 'Group orders that accumulate toward a target and close on a deadline, rather than selling from stock.' },
      { title: 'Share sizing', desc: 'Whole, half and smaller shares explained in terms of freezer space rather than weight tables.' },
      { title: 'Order tracking', desc: 'A tracking page so customers can follow an order from campaign close through processing to delivery.' },
      { title: 'Delivery scheduling', desc: 'Calgary-area delivery windows tied to when each campaign closes.' },
      { title: 'Editorial pages', desc: 'How it works, services, FAQ and About, written to answer the questions that otherwise become emails.' },
      { title: 'Light and dark themes', desc: 'A theme toggle, with both modes designed rather than one inverted.' },
    ],
    stack: ['React', 'Tailwind CSS', 'Responsive design', 'SEO'],
    services: ['web-development', 'software-development', 'ui-ux-design'],
    industries: ['retail-and-ecommerce'],
    outcomes: [],
  },
  {
    slug: 'shangeit',
    name: 'ShangeIt',
    url: 'https://shangeit.com/',
    sector: 'Real estate',
    year: '2026',
    shot: 'shangeit',
    tagline: 'A verified property marketplace for renters, buyers and agents in Nigeria',
    metaTitle: 'ShangeIt Case Study | Codelithic Labs',
    metaDescription:
      'How we built ShangeIt, a two-sided property marketplace for Nigeria with verified listings, agent accounts and a guided transaction flow.',
    summary:
      'ShangeIt lists verified homes for rent and sale across Nigeria. Its promise is in the strapline: trusted homes, verified people. In a market where listing fraud is the default worry, verification is the product.',
    brief: [
      'A property marketplace is two products wearing one coat. Renters and buyers need to search, filter and trust what they find. Agents need to list, manage and be verified. Both sides share a catalogue but almost nothing else, and the agent side carries the authentication and permissions.',
      'We built distinct account types for buyers and agents, a listing pipeline with verification built into it rather than bolted on, and a search that filters on the things people actually decide with: location, rent or buy, property type and price range.',
    ],
    built: [
      { title: 'Property search', desc: 'Filtering by location, rent or buy, property type and price range from the first screen.' },
      { title: 'Verified listings', desc: 'A listing pipeline with verification as a step, supporting the trust the brand is built on.' },
      { title: 'Two account types', desc: 'Separate buyer and agent journeys, with their own sign-up, sign-in and permissions.' },
      { title: 'Agent listing tools', desc: 'A list-a-property flow so agents can publish and manage their own inventory.' },
      { title: 'Property detail pages', desc: 'Individually addressable pages per property, so a listing can be shared and indexed.' },
      { title: 'Guided transaction flow', desc: 'A five-step path from discovering a property through to finalising the deal.' },
    ],
    stack: ['React', 'Vite', 'Authentication', 'Search & filtering'],
    services: ['web-development', 'software-development', 'ui-ux-design'],
    industries: ['professional-services'],
    outcomes: [],
  },
  {
    slug: 'iwickliffe',
    name: 'iWickliffe',
    url: 'https://iwickliffe.com/',
    sector: 'Retail & e-commerce',
    year: '2025',
    shot: 'iwickliffe',
    tagline: 'A jewellery storefront built around how people actually shop for gifts',
    metaTitle: 'iWickliffe Case Study | Codelithic Labs',
    metaDescription:
      'How we built iWickliffe, a jewellery and accessories storefront with a deep category structure, gifting journeys and a full cart and account flow.',
    summary:
      'iWickliffe sells handcrafted jewellery and accessories. Much of it is bought as a gift, which changes how the catalogue has to be organised: people arrive knowing who they are shopping for long before they know what they want.',
    brief: [
      'A jewellery catalogue can be sliced several ways at once. By product type, bracelets, necklaces, rings, anklets, earrings and arm cuffs. By who it is for, men or women. By collection, Alpha, Wired, Gemstone, Inspirational and Personalized. Force one hierarchy on all of that and most visitors end up somewhere they did not intend.',
      'We built overlapping navigation instead, so the same item is reachable from several routes, and added dedicated gifting journeys for the shoppers who start from a person rather than a product.',
    ],
    built: [
      { title: 'Multi-axis catalogue', desc: 'Products reachable by type, by audience and by collection rather than one forced hierarchy.' },
      { title: 'Gifting journeys', desc: 'Dedicated paths for gifts for her, for him, for mum and for dad.' },
      { title: 'Collections', desc: 'Curated ranges including Gemstone, Alpha, Wired, Inspirational and Personalized.' },
      { title: 'Cart and accounts', desc: 'Full cart, checkout and customer account flows.' },
      { title: 'Search', desc: 'Catalogue search for shoppers who arrive knowing exactly what they want.' },
      { title: 'Reviews and trust signals', desc: 'Customer reviews plus shipping, support and returns messaging where buyers hesitate.' },
    ],
    stack: ['React', 'E-commerce', 'Responsive design'],
    services: ['web-development', 'ui-ux-design', 'digital-marketing'],
    industries: ['retail-and-ecommerce'],
    outcomes: [],
  },
];
